import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import KnowledgeBaseAlert from '@/features/bot/KnowledgeBaseAlert';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import CopyVariable from '../../../../common/CopyVariable';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';
import { TextInputWithHint } from './TextInputWithHint';

interface IFormData extends INoteForm {
  name: string;
  variableName: string;
  value: string;
}

export const MacBotSummarizeConversationForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const [isDelete, setIsDelete] = useState(true);
  const userId = useAppSelector(selectUseId);

  const schema = Yup.object({
    name: Yup.string().max(30, t('Wizard/validationLabels/key16')).required(t('MacBotCreateVariableForm/scenario_variable_name_required')),
    variableName: Yup.string().required(t('MacBotCreateVariableForm/scenario_variable_required')),
    ...getNoteSchema(t),
  });
  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    control,
    getValues,
    watch,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'notes',
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.SummarizeConversation,
        parent: itemData.parent,
        title: data.name,
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.SummarizeConversation,
        parent: parentId,
        title: data.name,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        name: itemData?.title ?? '',
        variableName: itemData?.title ?? '',
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)} size="md">
        <ScenarioHeader
          type={MacBotActionKind.ShowSummarizeConversation}
          onClose={() => {
            reset();
          }}
        />
        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Col xs={12}>
              <TextInputWithHint
                label={t('MacBotResponseForm/key1')}
                name="name"
                register={register(`name`, {
                  onChange: e => {
                    setValue(`variableName`, `${e.target.value.trim().toLowerCase().replaceAll(' ', '_')}`);
                  },
                })}
                error={errors.name}
                max={31}
                maxLength={31}
                HintMessage={[t('MacBotMediaForm/maxSizeLenght', { size: 30 })]}
              />
            </Col>
          </Row>

          <KnowledgeBaseAlert texts={['SummarizeConversation/recommendation', 'SummarizeConversation/recommendation-2']} />

          <Row className="tw-mt-2 tw-items-center g-md-3 md:tw-gap-y-0 tw-gap-y-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotCreateVariableForm/key4')}
                </Form.Label>
                <Controller name="variableName" control={control} render={({ field: { value } }) => <CopyVariable text={value} noTitle />} />
              </Form.Group>
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-summarize-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
